import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import {
  adminCreateArticle,
  adminGetArticle,
  adminUpdateArticle,
  adminDeleteArticle,
} from "@/lib/api";

const CATEGORIES = [
  "Product Management",
  "Product Strategy",
  "Technical Solution",
  "Artificial Intelligence",
  "IoT & Hardware",
  "Smart Energy",
  "Software Engineering",
  "Community",
  "Insight",
];

const EMOJIS = ["✦", "◆", "◉", "◈", "◇", "◊", "✧", "⬡", "✺", "⟡"];

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const empty = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  category: CATEGORIES[0],
  read_time: "5 min",
  cover_emoji: "✦",
  status: "draft",
  published_at: new Date().toISOString().slice(0, 10),
  seo_title: "",
  seo_description: "",
  og_image_url: "",
  keywords: [],
};

const ArticleEditor = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [touchedSlug, setTouchedSlug] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const a = await adminGetArticle(id);
        setForm({
          title: a.title || "",
          slug: a.slug || "",
          excerpt: a.excerpt || "",
          content: a.content || "",
          category: a.category || CATEGORIES[0],
          read_time: a.read_time || "5 min",
          cover_emoji: a.cover_emoji || "✦",
          status: a.status || "draft",
          published_at: (a.published_at || "").slice(0, 10),
          seo_title: a.seo_title || "",
          seo_description: a.seo_description || "",
          og_image_url: a.og_image_url || "",
          keywords: a.keywords || [],
        });
        setTouchedSlug(true);
      } catch {
        toast.error("Gagal memuat artikel");
        navigate("/admin");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onTitle = (v) => {
    update("title", v);
    if (!touchedSlug && !isEdit) update("slug", slugify(v));
  };

  const onSlugChange = (v) => {
    setTouchedSlug(true);
    update("slug", slugify(v));
  };

  const save = async (publish = false) => {
    if (!form.title.trim() || !form.excerpt.trim()) {
      toast.error("Title dan excerpt wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        status: publish ? "published" : form.status,
        slug: form.slug || slugify(form.title),
      };
      if (isEdit) {
        await adminUpdateArticle(id, payload);
        toast.success(publish ? "Dipublikasikan" : "Perubahan disimpan");
      } else {
        await adminCreateArticle(payload);
        toast.success(publish ? "Artikel dipublikasikan" : "Draft dibuat");
      }
      navigate("/admin");
    } catch (e) {
      toast.error("Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm("Hapus artikel ini? Tindakan tidak dapat dibatalkan.")) return;
    try {
      await adminDeleteArticle(id);
      toast.success("Artikel dihapus");
      navigate("/admin");
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  if (loading) {
    return (
      <AdminLayout title={isEdit ? "Edit article" : "New article"}>
        <div className="text-sm text-slate-500" data-testid="editor-loading">Loading…</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title={isEdit ? "Edit article" : "New article"}
      action={
        <div className="flex items-center gap-2">
          {isEdit && (
            <button
              onClick={remove}
              data-testid="editor-delete-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 text-sm text-slate-300 hover:text-red-300 hover:border-red-400/40 hover:bg-red-500/[0.05] transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          )}
          <button
            onClick={() => save(false)}
            disabled={saving}
            data-testid="editor-save-draft-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 text-sm text-white hover:bg-white/[0.04] disabled:opacity-50 transition-colors"
          >
            <Save className="w-4 h-4" />
            Save draft
          </button>
          <button
            onClick={() => save(true)}
            disabled={saving}
            data-testid="editor-publish-btn"
            className="group inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-medium shadow-[0_0_24px_rgba(59,130,246,0.35)] hover:shadow-[0_0_40px_rgba(59,130,246,0.55)] disabled:opacity-60 transition-shadow"
          >
            <Send className="w-4 h-4" />
            {form.status === "published" && isEdit ? "Update" : "Publish"}
          </button>
        </div>
      }
    >
      <div className="mb-6">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
          data-testid="editor-back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to articles
        </Link>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        {/* Main editor */}
        <div className="rounded-2xl glass-elevated p-6 sm:p-8 space-y-5">
          <Field label="Title">
            <input
              data-testid="editor-title"
              value={form.title}
              onChange={(e) => onTitle(e.target.value)}
              placeholder="Judul artikel premium…"
              className="w-full bg-transparent text-2xl text-white placeholder:text-slate-600 outline-none border-b border-white/[0.06] focus:border-blue-400/50 pb-2 transition-colors"
              style={{ fontFamily: "Outfit" }}
            />
          </Field>

          <Field label="Slug" hint="URL-safe (otomatis dari title)">
            <div className="flex items-center gap-2">
              <span className="text-slate-500 font-mono-display text-sm">/blog/</span>
              <input
                data-testid="editor-slug"
                value={form.slug}
                onChange={(e) => onSlugChange(e.target.value)}
                placeholder="article-url"
                className="flex-1 bg-transparent text-sm text-slate-200 placeholder:text-slate-600 outline-none font-mono-display"
              />
            </div>
          </Field>

          <Field label="Excerpt" hint="Ringkasan singkat (1–2 kalimat)">
            <textarea
              data-testid="editor-excerpt"
              value={form.excerpt}
              onChange={(e) => update("excerpt", e.target.value)}
              rows={3}
              placeholder="Ringkasan yang menarik untuk pembaca…"
              className="w-full bg-transparent text-sm text-slate-200 placeholder:text-slate-600 outline-none resize-none"
            />
          </Field>

          <Field label="Content" hint="Markdown / plain text didukung">
            <textarea
              data-testid="editor-content"
              value={form.content}
              onChange={(e) => update("content", e.target.value)}
              rows={16}
              placeholder={`# Heading\n\nMulai tulis artikel Anda di sini…`}
              className="w-full bg-transparent text-sm text-slate-200 placeholder:text-slate-600 outline-none resize-y font-mono-display leading-relaxed"
            />
          </Field>
        </div>

        {/* Sidebar */}
        <aside className="space-y-5">
          <div className="rounded-2xl glass-elevated p-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-blue-400 font-mono-display mb-4">
              /// settings
            </div>

            <Field label="Status">
              <div className="grid grid-cols-2 gap-2">
                {["draft", "published"].map((s) => (
                  <button
                    key={s}
                    onClick={() => update("status", s)}
                    data-testid={`status-${s}`}
                    className={`px-3 py-2 rounded-lg text-xs uppercase tracking-[0.18em] font-mono-display border transition-colors ${
                      form.status === s
                        ? s === "published"
                          ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-200"
                          : "bg-amber-500/15 border-amber-400/40 text-amber-200"
                        : "border-white/10 text-slate-400 hover:text-white"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Category">
              <select
                data-testid="editor-category"
                value={form.category}
                onChange={(e) => update("category", e.target.value)}
                className="w-full bg-[#0a112a] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-200 outline-none focus:border-blue-400/40"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>

            <Field label="Read time">
              <input
                data-testid="editor-readtime"
                value={form.read_time}
                onChange={(e) => update("read_time", e.target.value)}
                placeholder="e.g. 5 min"
                className="w-full bg-[#0a112a] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-200 outline-none focus:border-blue-400/40"
              />
            </Field>

            <Field label="Publish date">
              <input
                type="date"
                data-testid="editor-publishdate"
                value={form.published_at}
                onChange={(e) => update("published_at", e.target.value)}
                className="w-full bg-[#0a112a] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-200 outline-none focus:border-blue-400/40"
              />
            </Field>

            <Field label="Cover emoji">
              <div className="flex flex-wrap gap-1.5">
                {EMOJIS.map((e) => (
                  <button
                    key={e}
                    onClick={() => update("cover_emoji", e)}
                    data-testid={`emoji-${e}`}
                    className={`w-9 h-9 rounded-lg grid place-items-center text-lg border transition-all ${
                      form.cover_emoji === e
                        ? "border-blue-400/60 bg-blue-500/15 text-blue-200 scale-105"
                        : "border-white/10 text-slate-400 hover:border-white/20 hover:text-white"
                    }`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </Field>
          </div>

          {/* SEO panel */}
          <div className="rounded-2xl glass-elevated p-5" data-testid="editor-seo-panel">
            <div className="text-[11px] uppercase tracking-[0.22em] text-blue-400 font-mono-display mb-4 flex items-center justify-between">
              <span>/// seo &amp; og</span>
              <span className="text-[9px] text-slate-500 normal-case tracking-normal">auto if empty</span>
            </div>

            <Field label="SEO title" hint={`${(form.seo_title || form.title || "").length}/60`}>
              <input
                data-testid="editor-seo-title"
                value={form.seo_title}
                onChange={(e) => update("seo_title", e.target.value)}
                placeholder={form.title || "Title untuk search engine…"}
                className="w-full bg-[#0a112a] border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 outline-none focus:border-blue-400/40"
              />
            </Field>

            <Field label="SEO description" hint={`${(form.seo_description || form.excerpt || "").length}/160`}>
              <textarea
                data-testid="editor-seo-description"
                value={form.seo_description}
                onChange={(e) => update("seo_description", e.target.value)}
                rows={3}
                placeholder={form.excerpt || "Description untuk search engine & link share…"}
                className="w-full bg-[#0a112a] border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 outline-none focus:border-blue-400/40 resize-none"
              />
            </Field>

            <Field label="Custom OG image URL" hint="kosongkan untuk auto-generate">
              <input
                data-testid="editor-og-image"
                value={form.og_image_url}
                onChange={(e) => update("og_image_url", e.target.value)}
                placeholder="https://…/cover.png"
                className="w-full bg-[#0a112a] border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 outline-none focus:border-blue-400/40 font-mono-display"
              />
            </Field>

            <Field label="Keywords" hint="pisahkan dengan koma">
              <input
                data-testid="editor-keywords"
                value={(form.keywords || []).join(", ")}
                onChange={(e) =>
                  update(
                    "keywords",
                    e.target.value
                      .split(",")
                      .map((k) => k.trim())
                      .filter(Boolean),
                  )
                }
                placeholder="iot, product, automation"
                className="w-full bg-[#0a112a] border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 outline-none focus:border-blue-400/40 font-mono-display"
              />
            </Field>

            {/* OG image preview */}
            {form.slug && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-mono-display mb-2">
                  og preview
                </div>
                <a
                  href={form.og_image_url || `${process.env.REACT_APP_BACKEND_URL}/api/og/${form.slug}.png`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block rounded-xl overflow-hidden border border-white/[0.06] hover:border-blue-400/40 transition-colors"
                  data-testid="editor-og-preview"
                >
                  <img
                    src={form.og_image_url || `${process.env.REACT_APP_BACKEND_URL}/api/og/${form.slug}.png`}
                    alt="OG preview"
                    className="w-full block"
                  />
                </a>
                <div className="mt-1.5 text-[10px] text-slate-500 font-mono-display">
                  open in new tab → preview
                </div>
              </div>
            )}
          </div>

          {/* Preview card */}
          <div className="rounded-2xl glass-elevated p-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-blue-400 font-mono-display mb-3">
              /// preview
            </div>
            <div className="rounded-xl border border-white/[0.06] p-4 bg-[#0a112a]/60">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-[0.18em] text-blue-200 font-mono-display">
                  {form.category}
                </span>
                <span className="text-blue-300/80 text-lg">{form.cover_emoji}</span>
              </div>
              <div className="text-white text-base font-medium leading-snug" style={{ fontFamily: "Outfit" }}>
                {form.title || "Untitled article"}
              </div>
              <p className="mt-2 text-xs text-slate-400 leading-relaxed line-clamp-3">
                {form.excerpt || "Excerpt akan muncul di sini…"}
              </p>
              <div className="mt-3 pt-3 border-t border-white/[0.06] text-[10px] text-slate-500 font-mono-display flex items-center justify-between">
                <span>{form.published_at}</span>
                <span>{form.read_time}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </AdminLayout>
  );
};

const Field = ({ label, hint, children }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <label className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-mono-display">
        {label}
      </label>
      {hint && <span className="text-[10px] text-slate-600 font-mono-display">{hint}</span>}
    </div>
    {children}
  </div>
);

export default ArticleEditor;
